import React, { useEffect, useMemo, useState } from "react"
import { compareDashboardKpis, getDashboardKpiComparisonOptions, DatoxComparisonError } from "@/datox/api"
import { aggregateKpiValue, applyDashboardFilters, applyWidgetTableSettings } from "./DatoxComplianceDashboard.jsx"
import "./kpi-comparison.css"
import { TbArrowUpRight, TbArrowDownRight, TbMinus } from "react-icons/tb"

export const DEFAULT_KPI_COMPARISON = Object.freeze({
  version: 1,
  mode: "previous_period",
  previous_dashboard_id: null,
  previous_widget_id: null,
  change: "both",
  direction: "higher_is_better",
})

export function isValidKpiComparison(config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) return false
  const candidate = config
  return (
    candidate.version === 1 &&
    candidate.mode === "previous_period" &&
    (candidate.previous_dashboard_id === null || typeof candidate.previous_dashboard_id === "string") &&
    (candidate.previous_widget_id === null || typeof candidate.previous_widget_id === "string") &&
    (candidate.change === "both" || candidate.change === "amount" || candidate.change === "percentage") &&
    (candidate.direction === "higher_is_better" ||
      candidate.direction === "lower_is_better" ||
      candidate.direction === "neutral")
  )
}

export function useDashboardKpiComparisons(input) {
  const enabled = input.enabled !== false
  const comparisonWidgets = useMemo(
    () => input.widgets.filter((widget) => widget.type === "kpi" && isValidKpiComparison(widget.kpi_comparison)),
    [input.widgets],
  )
  const requestState = useMemo(
    () => buildRequestState(comparisonWidgets, input.outputTables, input.activeFilterWidgets ?? [], input.labels),
    [comparisonWidgets, input.outputTables, input.activeFilterWidgets, input.labels],
  )
  const requestSignature = JSON.stringify({
    revision: input.revision,
    widgets: comparisonWidgets.map((widget) => {
      const current = requestState.current_values[widget.id]
      const selection = widget.kpi_comparison || {}
      return [
        widget.id,
        selection.previous_dashboard_id || null,
        selection.previous_widget_id || null,
        Number.isFinite(current) ? current : null,
      ]
    }),
  })
  const [options, setOptions] = useState(null)
  const [optionsError, setOptionsError] = useState(null)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [itemsByWidgetId, setItemsByWidgetId] = useState({})
  const [loadingComparisons, setLoadingComparisons] = useState(false)
  const [stale, setStale] = useState(false)
  const [staleMessage, setStaleMessage] = useState(null)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    let active = true
    if (!enabled || !input.projectID || !input.dashboardID) {
      setOptions(null)
      setOptionsError(null)
      setOptionsLoading(false)
      return
    }
    setOptionsLoading(true)
    setOptionsError(null)
    void getDashboardKpiComparisonOptions(input.projectID, input.dashboardID)
      .then((response) => {
        if (!active) return
        setOptions(response)
      })
      .catch((error) => {
        if (!active) return
        setOptions(null)
        setOptionsError(error instanceof Error ? error.message : input.labels.kpiComparisonSourcesUnavailable || "")
      })
      .finally(() => {
        if (active) setOptionsLoading(false)
      })
    return () => {
      active = false
    }
  }, [enabled, input.projectID, input.dashboardID, refreshToken])

  useEffect(() => {
    let active = true
    if (!enabled || !input.projectID || !input.dashboardID || input.revision <= 0 || !requestState.widget_ids.length) {
      setItemsByWidgetId(requestState.local)
      setLoadingComparisons(false)
      setStale(false)
      setStaleMessage(null)
      return
    }
    setLoadingComparisons(true)
    setStale(false)
    setStaleMessage(null)
    void compareDashboardKpis(input.projectID, input.dashboardID, {
      widget_ids: requestState.widget_ids,
      current_values: requestState.current_values,
      expected_revision: input.revision,
      selections: Object.fromEntries(
        comparisonWidgets.map((widget) => [
          widget.id,
          {
            previous_dashboard_id: widget.kpi_comparison?.previous_dashboard_id ?? null,
            previous_widget_id: widget.kpi_comparison?.previous_widget_id ?? null,
          },
        ]),
      ),
    })
      .then((response) => {
        if (!active) return
        const next = { ...requestState.local }
        for (const item of response.items)
          next[item.widget_id] = {
            ...item,
            previous_period_label: response.previous_period?.label ?? null,
            current_period_label: response.current_period?.label ?? null,
            state: item.reason ?? undefined,
          }
        setItemsByWidgetId(next)
      })
      .catch((error) => {
        if (!active) return
        if (error instanceof DatoxComparisonError && error.status === 409) {
          setStale(true)
          setStaleMessage(input.labels.kpiComparisonStaleReload || "")
          setItemsByWidgetId(
            Object.fromEntries(
              requestState.widget_ids.map((widgetId) => [
                widgetId,
                {
                  widget_id: widgetId,
                  status: "stale",
                  code: "stale_revision",
                  reason: input.labels.kpiComparisonStaleReload || "",
                  current_value: requestState.current_values[widgetId],
                  state: "stale",
                },
              ]),
            ),
          )
          return
        }
        setItemsByWidgetId(requestState.local)
      })
      .finally(() => {
        if (active) setLoadingComparisons(false)
      })
    return () => {
      active = false
    }
  }, [enabled, input.projectID, input.dashboardID, requestSignature, refreshToken])

  return {
    options,
    loading: optionsLoading,
    error: optionsError,
    refetch: () => setRefreshToken((value) => value + 1),
    itemsByWidgetId,
    loadingComparisons,
    stale,
    staleMessage,
  }
}

export function KpiComparisonSettings(props) {
  const config = isValidKpiComparison(props.widget.kpi_comparison) ? props.widget.kpi_comparison : undefined
  const active = !!config
  const availableDashboards = props.options?.previous_period ? props.options.dashboards : []
  const selectedDashboardID = config?.previous_dashboard_id ?? availableDashboards[0]?.id ?? null
  const selectedDashboard =
    availableDashboards.find((dashboard) => dashboard.id === selectedDashboardID) ?? availableDashboards[0]
  const widgetChoices = selectedDashboard?.widgets ?? []
  const selectedWidgetID = config?.previous_widget_id ?? widgetChoices[0]?.id ?? null
  const selectedWidget = widgetChoices.find((widget) => widget.id === selectedWidgetID)
  const message =
    props.stateMessage ||
    comparisonSourceMessage(props.labels, props.options, selectedDashboard, selectedWidget, config)

  useEffect(() => {
    if (!active || !availableDashboards.length) return
    const nextDashboard =
      availableDashboards.find((dashboard) => dashboard.id === selectedDashboardID) || availableDashboards[0]
    const nextWidget =
      nextDashboard?.widgets?.find((widget) => widget.id === selectedWidgetID) || nextDashboard?.widgets?.[0]
    if (!nextDashboard?.id || !nextWidget?.id) return
    if (config?.previous_dashboard_id === nextDashboard.id && config?.previous_widget_id === nextWidget.id) return
    props.onChange({
      ...(config || DEFAULT_KPI_COMPARISON),
      previous_dashboard_id: nextDashboard.id,
      previous_widget_id: nextWidget.id,
    })
  }, [
    active,
    availableDashboards,
    config?.previous_dashboard_id,
    config?.previous_widget_id,
    props.onChange,
    selectedDashboardID,
    selectedWidgetID,
  ])

  return (
    <div className="datox-react-dashboard__comparison-settings">
      {props.options && props.options.previous_period ? (
        <label
          style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, justifyContent: "flex-start" }}
        >
          <button
            type="button"
            role="switch"
            aria-checked={active}
            className={`kpi-switch ${active ? "kpi-switch--on" : ""}`}
            onClick={() => props.onChange(active ? undefined : { ...DEFAULT_KPI_COMPARISON })}
          >
            <span className="kpi-switch__thumb" />
          </button>
          <span>{props.labels.kpiComparisonEnabled}</span>
        </label>
      ) : null}
      {active ? (
        <>
          <label>
            <div className="datox-react-dashboard__field-label">{props.labels.kpiComparisonPreviousDashboard}</div>
            <select
              className="datox-react-dashboard__select"
              value={selectedDashboardID || ""}
              onChange={(event) => {
                const nextDashboard = availableDashboards.find(
                  (dashboard) => dashboard.id === event.currentTarget.value,
                )
                const nextWidget = nextDashboard?.widgets?.[0]
                props.onChange({
                  ...(config || DEFAULT_KPI_COMPARISON),
                  previous_dashboard_id: nextDashboard?.id ?? null,
                  previous_widget_id: nextWidget?.id ?? null,
                })
              }}
              disabled={!availableDashboards.length}
            >
              {availableDashboards.map((dashboard) => (
                <option key={dashboard.id} value={dashboard.id}>
                  {dashboard.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <div className="datox-react-dashboard__field-label">{props.labels.kpiComparisonPreviousKpi}</div>
            <select
              className="datox-react-dashboard__select"
              value={selectedWidgetID || ""}
              onChange={(event) =>
                props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), previous_widget_id: event.currentTarget.value })
              }
              disabled={!widgetChoices.length}
            >
              {widgetChoices.map((widget) => (
                <option key={widget.id} value={widget.id}>
                  {widget.title}
                </option>
              ))}
            </select>
          </label>
          <SegmentedSection
            label={props.labels.kpiComparisonChangeMode}
            value={config?.change || DEFAULT_KPI_COMPARISON.change}
            options={[
              { value: "both", label: props.labels.kpiComparisonBoth },
              { value: "amount", label: props.labels.kpiComparisonAmount },
              { value: "percentage", label: props.labels.kpiComparisonPercentage },
            ]}
            onChange={(change) => props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), change })}
          />
          <SegmentedSection
            label={props.labels.kpiComparisonDirection}
            value={config?.direction || DEFAULT_KPI_COMPARISON.direction}
            options={[
              { value: "higher_is_better", label: props.labels.kpiComparisonHigherIsBetter },
              { value: "lower_is_better", label: props.labels.kpiComparisonLowerIsBetter },
              { value: "neutral", label: props.labels.kpiComparisonNeutral },
            ]}
            onChange={(direction) => props.onChange({ ...(config || DEFAULT_KPI_COMPARISON), direction })}
          />
          {message ? <div className="datox-react-dashboard__comparison-state">{message}</div> : null}
        </>
      ) : null}
      {props.isStale ? (
        <button
          type="button"
          className="datox-react-dashboard__button datox-react-dashboard__button--outline-primary datox-react-dashboard__comparison-reload"
          onClick={props.onReload}
        >
          {props.labels.kpiComparisonReload}
        </button>
      ) : null}
    </div>
  )
}

export function KpiComparisonDisplay(props) {
  const placement = props.placement || "caption"
  const config = isValidKpiComparison(props.widget.kpi_comparison) ? props.widget.kpi_comparison : null
  if (!config || !props.result) return null

  if (props.result.status !== "available") {
    // badge joyida xatolik ko'rsatmaymiz (OX Flow kabi), faqat bottom'da
    if (placement === "badge") return null
    return (
      <div className="datox-react-dashboard__comparison-state">{comparisonStateText(props.labels, props.result)}</div>
    )
  }

  const amount = props.result.amount_change ?? 0
  const previous = props.result.previous_value ?? 0
  const pct = props.result.percentage_change
  const tone = trendTone(config.direction, amount)
  const previousLabel = props.result.previous_period_label
  const vsPrevious = props.labels.kpiComparisonVsPrevious || "Vs Previous Period"

  if (placement === "caption") {
    return (
      <div className="datox-react-dashboard__comparison-caption">
        {vsPrevious}
        {previousLabel ? ` - ${previousLabel}` : ""}
      </div>
    )
  }
  const suffix = props.widget.style?.valueFormat === "percent" ? " pp" : ""
  const relativeAvailable = previous > 0 && pct != null && Number.isFinite(pct)
  const percentageAbs = relativeAvailable ? `${formatMagnitude(Math.abs(pct), props.widget.style, 2)}%` : null
  const amountAbs = `${formatMagnitude(Math.abs(amount), props.widget.style)}${suffix}`
  const change = config.change !== "amount" && percentageAbs !== null ? percentageAbs : amountAbs

  const Arrow = amount > 0 ? TbArrowUpRight : amount < 0 ? TbArrowDownRight : TbMinus
  const movement = amount > 0 ? "Up" : amount < 0 ? "Down" : "Unchanged"

  const sign = amount > 0 ? "+" : amount < 0 ? "-" : ""
  const previousFmt = formatMagnitude(previous, props.widget.style)
  const currentFmt = formatMagnitude(props.result.current_value ?? 0, props.widget.style)
  const details =
    `${vsPrevious}${previousLabel ? ` (${previousLabel})` : ""}: ${previousFmt}; ` +
    `${props.result.current_period_label ? `${props.result.current_period_label}: ` : "Current: "}${currentFmt}; ` +
    `Change: ${sign}${amountAbs}${percentageAbs !== null ? ` (${sign}${percentageAbs})` : ""}`

  return (
    <span
      className="datox-react-dashboard__comparison-badge"
      data-comparison-direction={tone}
      aria-label={`${movement} ${change}. ${details}`}
      title={details}
    >
      <Arrow size={14} aria-hidden="true" />
      <span className="datox-react-dashboard__comparison-badge-value">{change}</span>
    </span>
  )
}

function buildRequestState(widgets, outputTables, filterWidgets, labels) {
  const current_values = {}
  const local = {}
  const widget_ids = []
  widgets.forEach((widget) => {
    const table = widgetTable(widget, outputTables, filterWidgets)
    const current = aggregateKpiValue(table, widget.data || {})
    if (!Number.isFinite(current)) {
      local[widget.id] = {
        widget_id: widget.id,
        status: "unavailable",
        code: "current_widget_unavailable",
        reason: labels.kpiComparisonCurrentUnavailable || "",
        current_value: 0,
        state: "current_widget_unavailable",
      }
      return
    }
    widget_ids.push(widget.id)
    current_values[widget.id] = current
  })
  return { widget_ids, current_values, local }
}

function widgetTable(widget, outputTables, filterWidgets) {
  const table = outputTables.find((item) => item.datasetID === widget.data?.dataset_id)
  return applyWidgetTableSettings(applyDashboardFilters(table, widget, filterWidgets), widget.data || {})
}

function comparisonSourceMessage(labels, options, dashboard, widget, config) {
  if (!options || options.previous_period == null) return labels.kpiComparisonNoPreviousPeriod
  if (!options.dashboards.length) return labels.kpiComparisonPreviousNotPublished
  if (config?.previous_dashboard_id && !dashboard) return labels.kpiComparisonPreviousDashboardMissing
  if (config?.previous_widget_id && !widget) return labels.kpiComparisonNoPublishedValue
  return ""
}

function comparisonStateText(labels, result) {
  if (result.status === "stale") return labels.kpiComparisonStale
  if (result.code === "selection_required") return labels.kpiComparisonNeedsSelection
  if (result.code === "unit_mismatch") return labels.kpiComparisonUnitMismatch
  if (result.code === "previous_value_missing") return labels.kpiComparisonNoPublishedValue
  if (result.code === "current_widget_unavailable") return labels.kpiComparisonCurrentUnavailable
  return result.reason || labels.kpiComparisonUnavailable
}

function trendTone(direction, amount) {
  if (!amount) return "neutral"
  if (direction === "neutral") return "neutral"
  const positive = amount > 0
  return (direction === "higher_is_better" && positive) || (direction === "lower_is_better" && !positive)
    ? "positive"
    : "negative"
}

function formatMagnitude(value, style, forceDecimals) {
  const decimals = Number.isFinite(forceDecimals)
    ? forceDecimals
    : Number.isFinite(Number(style?.decimalPlaces))
      ? Number(style?.decimalPlaces)
      : 2
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(Number(value)))
}

function SegmentedSection(props) {
  return (
    <div className="datox-react-dashboard__comparison-segmented">
      <div className="datox-react-dashboard__field-label">{props.label}</div>
      <div className="datox-react-dashboard__comparison-segmented-row">
        {props.options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={
              option.value === props.value
                ? "datox-react-dashboard__comparison-segmented-item datox-react-dashboard__comparison-segmented-item--active"
                : "datox-react-dashboard__comparison-segmented-item"
            }
            onClick={() => props.onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
